import { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import AdminLayout from '../components/AdminLayout';
import { useAccentColor } from '@/context/AccentColorContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';
import { useSiteContent } from '@/context/SiteContentContext';
import { useSystemSettings } from '@/context/SystemSettingsContext';
import { defaultSystemSettings } from '@/lib/api/systemSettings';

const PRESET_COLORS = [
  { label: 'Sky Blue', value: '#0EA5E9' },
  { label: 'Ocean Blue', value: '#0284C7' },
  { label: 'Indigo', value: '#4F46E5' },
  { label: 'Teal', value: '#0D9488' },
  { label: 'Emerald', value: '#059669' },
  { label: 'Orange', value: '#F97316' },
  { label: 'Rose', value: '#E11D48' },
  { label: 'Violet', value: '#7C3AED' },
];

function SettingsContent() {
  const { theme, toggleTheme } = useTheme();
  const { colors, setDarkAccent, setLightAccent } = useAccentColor();
  const { settings, allEnabled, setBadgeVisibility, setAllBadges, resetBadgeVisibility } = useBadgeVisibility();
  const { content, updateContent } = useSiteContent();
  const { settings: systemSettings, loaded: systemSettingsLoaded, saveMaintenanceSettings } = useSystemSettings();
  const dark = theme === 'dark';
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(defaultSystemSettings.maintenance.enabled);
  const [maintenanceTitle, setMaintenanceTitle] = useState(defaultSystemSettings.maintenance.title);
  const [maintenanceMessage, setMaintenanceMessage] = useState(defaultSystemSettings.maintenance.message);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [maintenanceNotice, setMaintenanceNotice] = useState('');
  const [maintenanceError, setMaintenanceError] = useState('');

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[var(--accent)]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[var(--accent)]';

  const currentAccent = dark ? colors.dark : colors.light;
  const setCurrentAccent = dark ? setDarkAccent : setLightAccent;
  const badgeItems: Array<{ key: keyof typeof settings; title: string; desc: string }> = [
    { key: 'heroBadge', title: 'Hero Badge', desc: 'Homepage hero üstündeki ana badge etiketi.' },
    { key: 'aboutBadge', title: 'About Badges', desc: 'Homepage About section ve About page hero badge etiketleri.' },
    { key: 'animationBadge', title: 'Animation Badge', desc: 'About sayfasındaki Stingray animation section badge’i.' },
    { key: 'testimonialsBadge', title: 'Testimonials Badge', desc: 'Homepage testimonials section badge etiketi.' },
    { key: 'launchBadges', title: 'Launch Badges', desc: 'Product launch alanındaki badge, ribbon ve available now etiketleri.' },
    { key: 'productBadges', title: 'Product Badges', desc: 'Homepage lineup, products catalog, compare, detail ve ürün kartlarındaki badge’ler.' },
    { key: 'wikiBadges', title: 'Wiki Badges', desc: 'Tech Wiki içindeki knowledge base ve article badge etiketleri.' },
    { key: 'contactBadge', title: 'Contact Badge', desc: 'Contact Us sayfasındaki hero badge etiketi.' },
    { key: 'faqBadge', title: 'FAQ Badge', desc: 'FAQ sayfasındaki Help Center badge etiketi.' },
    { key: 'legalBadges', title: 'Legal Badges', desc: 'Warranty, Terms ve Privacy sayfalarının hero badge etiketleri.' },
    { key: 'footerWarrantyBadges', title: 'Footer Warranty Badges', desc: 'Footer altındaki warranty ve distribution badge grubu.' },
  ];
  const enabledBadgeCount = badgeItems.filter((item) => settings[item.key]).length;
  const allDisabled = enabledBadgeCount === 0;
  const socialItems: Array<{ key: keyof typeof content.footer.socials; label: string; placeholder: string; icon: string }> = [
    { key: 'facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/your-page', icon: 'ri-facebook-fill' },
    { key: 'instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/your-profile', icon: 'ri-instagram-line' },
    { key: 'youtube', label: 'YouTube URL', placeholder: 'https://youtube.com/@yourchannel', icon: 'ri-youtube-line' },
    { key: 'linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/company/your-company', icon: 'ri-linkedin-box-fill' },
  ];

  const switchBg = (enabled: boolean) =>
    enabled ? 'var(--accent)' : dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb';

  const updateSocialLink = (key: keyof typeof content.footer.socials, value: string) => {
    updateContent({
      ...content,
      footer: {
        ...content.footer,
        socials: {
          ...content.footer.socials,
          [key]: value,
        },
      },
    });
  };

  useEffect(() => {
    setMaintenanceEnabled(systemSettings.maintenance.enabled);
    setMaintenanceTitle(systemSettings.maintenance.title);
    setMaintenanceMessage(systemSettings.maintenance.message);
  }, [
    systemSettings.maintenance.enabled,
    systemSettings.maintenance.message,
    systemSettings.maintenance.title,
    systemSettings.maintenance.updatedAt,
  ]);

  const resetMaintenanceCopy = () => {
    setMaintenanceTitle(defaultSystemSettings.maintenance.title);
    setMaintenanceMessage(defaultSystemSettings.maintenance.message);
  };

  const handleSaveMaintenance = async () => {
    setMaintenanceSaving(true);
    setMaintenanceError('');
    setMaintenanceNotice('');

    try {
      await saveMaintenanceSettings({
        enabled: maintenanceEnabled,
        title: maintenanceTitle,
        message: maintenanceMessage,
      });
      setMaintenanceNotice(
        maintenanceEnabled
          ? 'Maintenance mode is now live for public visitors.'
          : 'Maintenance mode has been disabled and the site is live again.',
      );
    } catch (error) {
      setMaintenanceError(error instanceof Error ? error.message : 'Unable to save maintenance settings.');
    } finally {
      setMaintenanceSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Appearance */}
      <div className={`${card} rounded-lg p-6`}>
        <h2 className={`font-bold text-sm mb-4 ${text}`}>Appearance</h2>
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className={`text-sm font-medium ${text}`}>Theme</p>
            <p className={`text-xs ${muted} mt-0.5`}>Switch between dark and light mode for the admin panel</p>
          </div>
          <button
            onClick={toggleTheme}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
              dark
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={dark ? 'ri-sun-line' : 'ri-moon-line'}></i>
            </div>
            Switch to {dark ? 'Light' : 'Dark'} Mode
          </button>
        </div>

        {/* Accent Color */}
        <div>
          <p className={`text-sm font-medium mb-1 ${text}`}>Accent Color</p>
          <p className={`text-xs ${muted} mb-4`}>
            Choose the primary accent color for <strong>{dark ? 'Dark' : 'Light'}</strong> mode. Changes apply instantly across the entire site.
          </p>

          {/* Preset swatches */}
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.value}
                onClick={() => setCurrentAccent(c.value)}
                title={c.label}
                className="w-8 h-8 rounded-full cursor-pointer transition-all hover:scale-110 flex items-center justify-center"
                style={{ backgroundColor: c.value, outline: currentAccent === c.value ? `3px solid ${c.value}` : 'none', outlineOffset: 2 }}
              >
                {currentAccent === c.value && (
                  <i className="ri-check-line text-white text-xs font-black"></i>
                )}
              </button>
            ))}
          </div>

          {/* Custom color picker */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="color"
                value={currentAccent}
                onChange={(e) => setCurrentAccent(e.target.value)}
                className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0.5 bg-transparent"
                title="Custom color"
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                value={currentAccent}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setCurrentAccent(v);
                }}
                className={`w-full border rounded-lg px-3 py-2 text-sm outline-none transition-colors font-mono ${inputCls}`}
                placeholder="#0EA5E9"
              />
            </div>
            <div className="w-10 h-10 rounded-lg shrink-0" style={{ backgroundColor: currentAccent }}></div>
          </div>

          {/* Both theme colors preview */}
          <div className={`mt-4 p-3 rounded-lg flex items-center gap-4 ${dark ? 'bg-[#0D1B2A]' : 'bg-gray-50'}`}>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full" style={{ backgroundColor: colors.dark }}></div>
              <span className={`text-xs ${muted}`}>Dark: <span className="font-mono font-bold">{colors.dark}</span></span>
            </div>
            <div className="w-px h-4 bg-current opacity-20"></div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full" style={{ backgroundColor: colors.light }}></div>
              <span className={`text-xs ${muted}`}>Light: <span className="font-mono font-bold">{colors.light}</span></span>
            </div>
            <button
              onClick={() => {
                setDarkAccent(currentAccent);
                setLightAccent(currentAccent);
              }}
              className={`ml-auto text-xs px-3 py-1 rounded-lg cursor-pointer whitespace-nowrap transition-colors ${dark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
            >
              Apply to both
            </button>
          </div>
        </div>
      </div>

      {/* Badge Controls */}
      <div className={`${card} rounded-lg p-6`}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className={`font-bold text-sm ${text}`}>Badge Controls</h2>
            <p className={`text-xs mt-1 ${muted}`}>Sitedeki badge alanlarını buradan tek tek ya da topluca açıp kapatabilirsiniz.</p>
          </div>
          <button
            onClick={resetBadgeVisibility}
            className={`text-xs px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors ${dark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Reset Defaults
          </button>
        </div>

        <div className={`rounded-lg border p-4 mb-4 ${dark ? 'border-white/10 bg-[#0D1B2A]' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <p className={`text-sm font-semibold ${text}`}>Bulk Actions</p>
              <p className={`text-xs mt-0.5 ${muted}`}>
                {enabledBadgeCount}/{badgeItems.length} badge groups are currently visible.
              </p>
            </div>
            <div className={`text-xs font-semibold ${allEnabled ? 'text-green-500' : allDisabled ? 'text-red-400' : muted}`}>
              {allEnabled ? 'All enabled' : allDisabled ? 'All disabled' : 'Custom mix'}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setAllBadges(true)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                allEnabled
                  ? 'text-white'
                  : dark
                  ? 'bg-white/10 text-white/70 hover:bg-white/20'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
              style={allEnabled ? { backgroundColor: 'var(--accent)' } : undefined}
            >
              Enable All
            </button>
            <button
              onClick={() => setAllBadges(false)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                allDisabled
                  ? 'text-white'
                  : dark
                  ? 'bg-white/10 text-white/70 hover:bg-white/20'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
              style={allDisabled ? { backgroundColor: 'var(--accent)' } : undefined}
            >
              Disable All
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {badgeItems.map((item) => (
            <div
              key={item.key}
              className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${dark ? 'border-white/10 bg-[#0D1B2A]' : 'border-gray-200 bg-gray-50'}`}
            >
              <div>
                <p className={`text-sm font-semibold ${text}`}>{item.title}</p>
                <p className={`text-xs mt-0.5 ${muted}`}>{item.desc}</p>
              </div>
              <button
                onClick={() => setBadgeVisibility(item.key, !settings[item.key])}
                className="relative w-12 h-6 rounded-full transition-colors cursor-pointer shrink-0"
                style={{ backgroundColor: switchBg(settings[item.key]) }}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${settings[item.key] ? 'left-7' : 'left-1'}`}></span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className={`${card} rounded-lg p-6`}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className={`font-bold text-sm ${text}`}>Maintenance Mode</h2>
            <p className={`text-xs mt-1 ${muted}`}>
              Enable olduğunda admin sayfaları haricindeki ziyaretçiler zarif bir maintenance ekranı görür. Admin panel açık kalır, bu yüzden modu buradan tekrar kapatabilirsiniz.
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest ${
              maintenanceEnabled
                ? 'text-white'
                : dark
                ? 'bg-white/10 text-white/60'
                : 'bg-gray-100 text-gray-500'
            }`}
            style={maintenanceEnabled ? { backgroundColor: 'var(--accent)' } : undefined}
          >
            <i className={maintenanceEnabled ? 'ri-tools-fill' : 'ri-checkbox-circle-line'}></i>
            {maintenanceEnabled ? 'Maintenance On' : 'Site Live'}
          </span>
        </div>

        <div className={`rounded-lg border p-4 mb-4 ${dark ? 'border-white/10 bg-[#0D1B2A]' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className={`text-sm font-semibold ${text}`}>Public Site Status</p>
              <p className={`text-xs mt-0.5 ${muted}`}>
                {maintenanceEnabled
                  ? 'Homepage, products, contact, dealer pages and other public routes will show the maintenance screen.'
                  : 'Visitors will see the normal live website.'}
              </p>
            </div>
            <button
              onClick={() => setMaintenanceEnabled((current) => !current)}
              disabled={!systemSettingsLoaded}
              className="relative w-12 h-6 rounded-full transition-colors cursor-pointer shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: switchBg(maintenanceEnabled) }}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${maintenanceEnabled ? 'left-7' : 'left-1'}`}></span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Maintenance Headline</label>
            <input
              value={maintenanceTitle}
              onChange={(e) => setMaintenanceTitle(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`}
              placeholder="Scheduled Maintenance"
              disabled={!systemSettingsLoaded}
            />
          </div>
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Maintenance Message</label>
            <textarea
              rows={4}
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`}
              placeholder="Tell visitors the site is under maintenance and will be back shortly."
              disabled={!systemSettingsLoaded}
            />
          </div>
        </div>

        <div className={`mt-4 rounded-lg border p-4 ${dark ? 'border-white/10 bg-[#0D1B2A]' : 'border-gray-200 bg-gray-50'}`}>
          <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${muted}`}>Live Preview</p>
          <p className={`text-xl font-black mb-2 ${text}`}>{maintenanceTitle || defaultSystemSettings.maintenance.title}</p>
          <p className={`text-sm leading-relaxed ${muted}`}>
            {maintenanceMessage || defaultSystemSettings.maintenance.message}
          </p>
        </div>

        {maintenanceError && (
          <p className={`mt-4 rounded-lg border px-3 py-2 text-xs font-medium ${dark ? 'border-red-500/20 bg-red-500/10 text-red-300' : 'border-red-200 bg-red-50 text-red-600'}`}>
            {maintenanceError}
          </p>
        )}
        {maintenanceNotice && (
          <p className={`mt-4 rounded-lg border px-3 py-2 text-xs font-medium ${dark ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-600'}`}>
            {maintenanceNotice}
          </p>
        )}

        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <button
            onClick={resetMaintenanceCopy}
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-colors ${
              dark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            disabled={!systemSettingsLoaded || maintenanceSaving}
          >
            Use Default Copy
          </button>
          <button
            onClick={() => void handleSaveMaintenance()}
            className="text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--accent)' }}
            disabled={!systemSettingsLoaded || maintenanceSaving}
          >
            {maintenanceSaving ? 'Saving...' : 'Save Maintenance Settings'}
          </button>
        </div>
      </div>

      <div className={`${card} rounded-lg p-6`}>
        <div className="mb-4">
          <h2 className={`font-bold text-sm ${text}`}>Social Media Links</h2>
          <p className={`text-xs mt-1 ${muted}`}>Footer’daki sosyal medya ikonlarını buradan doğru hesaplara bağlayabilirsiniz. Değişiklikler anında kaydolur.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {socialItems.map((item) => (
            <div key={item.key}>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>{item.label}</label>
              <div className="relative">
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`}>
                  <i className={item.icon}></i>
                </span>
                <input
                  value={content.footer.socials[item.key]}
                  onChange={(e) => updateSocialLink(item.key, e.target.value)}
                  className={`w-full border rounded-lg pl-10 pr-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`}
                  placeholder={item.placeholder}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Company Info */}
      <div className={`${card} rounded-lg p-6`}>
        <h2 className={`font-bold text-sm mb-4 ${text}`}>Company Information</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Company Name</label>
              <input defaultValue="Cool Tech Marine Air Conditioning LLC" className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Brand Name</label>
              <input defaultValue="Stingray" className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
          </div>
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Address</label>
            <input defaultValue="30 N Gould St Ste R, Sheridan, WY 82801, USA" className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Phone</label>
              <input defaultValue="+1 307 381 5172" className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Email</label>
              <input defaultValue="info@stingrayenergy.net" className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
          </div>
          <button
            className="text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSettingsPage() {
  return (
    <AdminLayout title="Settings">
      <SettingsContent />
    </AdminLayout>
  );
}
